import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { NgCommonModule } from '@principle-theorem/ng-shared';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  IPatient,
  IReferral,
  IReferralMedia,
  ReferralActor,
} from '@principle-theorem/principle-core/interfaces';
import {
  INamedDocument,
  WithRef,
  isSameIdentifier,
} from '@principle-theorem/shared';
import { ReferralMediaService } from '../../services/referral-media.service';

@Component({
  selector: 'pr-referral-media-menu',
  imports: [
    NgCommonModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
  ],
  templateUrl: './referral-media-menu.component.html',
  styleUrl: './referral-media-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralMediaMenuComponent {
  private _referralMediaService = inject(ReferralMediaService);

  media = input.required<WithRef<IReferralMedia>[]>();
  referral = input.required<WithRef<IReferral>>();
  patient = input.required<INamedDocument<IPatient>>();
  currentUser = input.required<ReferralActor>();
  canManagePatientMedia = input<boolean>(false);

  canCopyToPatientFiles = computed(() => {
    if (!this.canManagePatientMedia()) {
      return false;
    }
    return this.media().some((item) => !item.patientMediaRef);
  });

  canDelete = computed(() => {
    if (!this.canManagePatientMedia()) {
      return false;
    }
    return this.media().every((item) =>
      isSameIdentifier(item.actor, this.currentUser())
    );
  });

  async download(): Promise<void> {
    await this._referralMediaService.downloadMedia(this.media());
  }

  async copyToPatientFiles(): Promise<void> {
    if (!this.canCopyToPatientFiles()) {
      return;
    }

    await this._referralMediaService.copyMediaToPatientWithValidation(
      this.patient(),
      this.media()
    );
  }

  async delete(): Promise<void> {
    if (!this.canDelete()) {
      return;
    }

    await this._referralMediaService.deleteMediaWithConfirmation(
      this.media(),
      this.referral(),
      this.currentUser()
    );
  }
}
